import React, { useState } from 'react';

export default function HowItWorks() {
  const [role, setRole] = useState('User');

  const renderContent = () => {
    if (role === 'User') {
      return (
        <>
          <h3 className="section-title">How It Works for Users</h3>
          <p className="section-subtitle">
            Ban Appeals makes it simple to appeal supported moderation actions directly through Discord.
          </p>
          <div className="how-note">
            <strong>Note:</strong> You must share a server with Ban Appeals (in addition to the one where the
            moderation action happened) and keep your DMs open in that server to receive messages from the bot.
            If you did not receive the notification message, you may still be able to submit a supported appeal
            via the <em>My Appeals</em> tab on this website.
          </div>
          <ol className="how-list">
            <li>
              <strong>Notification in DMs:</strong> When you are banned, timed out, or muted in a server using
              Ban Appeals, and that appeal form is enabled, you may receive a direct message with the action
              details and a button to start an appeal.
            </li>
            <li>
              <strong>Appeal via modal:</strong> After waiting any required wait time set by the server (if
              applicable), you&apos;ll be able to click the appeal button and submit your responses to that
              server&apos;s appeal questions in the modal. Do not submit any passwords or sensitive information.
            </li>
            <li>
              <strong>Final decision notification:</strong> Once server staff make a decision on your appeal,
              you will receive a notification with the outcome. Note that you cannot send any further replies
              or follow-ups through that appeal thread.
            </li>
          </ol>
        </>
      );
    }

    if (role === 'Mod') {
      return (
        <>
          <h3 className="section-title">How It Works for Mods</h3>
          <p className="section-subtitle">
            Moderators handle incoming appeals directly in the server, with minimal overhead.
          </p>
          <ol className="how-list">
            <li>
              <strong>Appeals appear automatically:</strong> When a user submits a ban, timeout, or mute appeal,
              it will automatically post in the configured appeal channel for that form along with the user&apos;s
              responses and the relevant action details.
            </li>
            <li>
              <strong>One-click decisions:</strong> To resolve an appeal, simply click <em>Accept</em> or{' '}
              <em>Deny</em>. Ban Appeals takes care of the rest, including notifying the user and reversing the
              action where applicable.
            </li>
          </ol>
        </>
      );
    }

    // Admin content
    return (
      <>
        <h3 className="section-title">How It Works for Admins</h3>
        <p className="section-subtitle">
          Configure each appeal form through one guided workflow.
        </p>

        <div className="how-note">
          <strong>Tip:</strong> All configuration settings can also be managed via the{' '}
          <em>My Servers</em> tab on this website.
        </div>

        <ol className="how-list">
          <li>
            <strong>Open <code>/forms</code> and choose a form:</strong> Run <code>/forms</code>, then choose
            whether you want to manage the <strong>Ban</strong>, <strong>Timeout</strong>, or{' '}
            <strong>Mute</strong> appeal form. Each form is configured separately.
          </li>
          <li>
            <strong>Configure that form&apos;s settings:</strong> Inside the form menu, set the{' '}
            <strong>appeal channel</strong>, choose any <strong>minimum wait time</strong>, and review whether
            the form is enabled. For the mute form, you must also select the <strong>muted role</strong>.
          </li>
          <li>
            <strong>Customize questions and messages:</strong> Each form has its own{' '}
            <strong>Questions</strong> and <strong>Messages</strong> sections. You can set 1–5 appeal questions
            and customize the notification, accepted, and denied messages for that specific form.
          </li>
          <li>
            <strong>Enable each form individually:</strong> When a form is ready, enable it from that form&apos;s{' '}
            <strong>Settings</strong> page. Ban appeals, timeout appeals, and mute appeals can each be turned on
            or off independently.
          </li>
          <li>
            <strong>Optionally configure join DMs with <code>/dmonjoin</code>:</strong> Use{' '}
            <code>/dmonjoin</code> to control whether the bot messages users when they first join your server,
            and to customize that join message.
          </li>
        </ol>
      </>
    );
  };

  return (
    <div className="home-hero">
      {/* Left: text + selector */}
      <div className="hero-left">
        <div className="how-header">
          <div>
            <h2 className="hero-title">How Ban Appeals Works</h2>
            <p className="hero-subtitle">
              Learn how Ban Appeals streamlines the appeal process for users, moderators, and admins.
            </p>
          </div>

          <div className="how-selector">
            <label htmlFor="role-select" className="how-label">
              I am a:
            </label>
            <select
              id="role-select"
              className="how-select"
              value={role}
              onChange={(e) => setRole(e.target.value)}
            >
              <option value="User">User</option>
              <option value="Mod">Mod</option>
              <option value="Admin">Admin</option>
            </select>
          </div>
        </div>

        <p className="how-role-note muted">
          <strong>Role definitions:</strong> Mods are users with the permission needed to review a form, such as{' '}
          <code>Ban Members</code>, <code>Moderate Members</code>, or <code>Manage Roles</code>. Admins are users
          with the <code>Manage Server</code> permission.
        </p>

        <div className="how-content">{renderContent()}</div>
      </div>

      {/* Right: image, matching home layout */}
      <div className="hero-right">
        <div className="hero-mascot-wrap">
          <div className="hero-mascot-glow" />
          <img
            src="/mascot.png"
            alt="Ban Appeals"
            className="hero-mascot"
          />
        </div>
      </div>
    </div>
  );
}